import axios from 'axios';

// Add a global interceptor to attach the JWT token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ============ AI MODELS ============

// Get all AI models
export const getAIModels = async () => {
  const response = await axios.get(`${API_URL}/ai/models`);
  return response.data;
};

// Get a single AI model
export const getAIModel = async (id) => {
  const response = await axios.get(`${API_URL}/ai/models/${id}`);
  return response.data;
};

// Create a new AI model
export const createAIModel = async (modelData) => {
  const response = await axios.post(`${API_URL}/ai/models`, modelData);
  return response.data;
};

// Update an AI model
export const updateAIModel = async (id, modelData) => {
  const response = await axios.put(`${API_URL}/ai/models/${id}`, modelData);
  return response.data;
};

// Delete an AI model
export const deleteAIModel = async (id) => {
  const response = await axios.delete(`${API_URL}/ai/models/${id}`);
  return response.data;
};

// Check AI model health
export const checkAIModelHealth = async (id) => {
  const response = await axios.post(`${API_URL}/ai/models/${id}/health-check`);
  return response.data;
};

// ============ AI PROMPTS ============

// Get all AI prompts for a tenant
export const getAIPrompts = async () => {
  const response = await axios.get(`${API_URL}/ai/prompts`);
  return response.data;
};

// Get prompts by campaign
export const getAIPromptsByCampaign = async (tenantId, campaignId) => {
  const response = await axios.get(`${API_URL}/ai/prompts/campaign`, {
    params: { tenantId, campaignId }
  });
  return response.data;
};

// Get a single AI prompt
export const getAIPrompt = async (id) => {
  const response = await axios.get(`${API_URL}/ai/prompts/${id}`);
  return response.data;
};

// Create a new AI prompt
export const createAIPrompt = async (promptData) => {
  const response = await axios.post(`${API_URL}/ai/prompts`, promptData);
  return response.data;
};

// Update an AI prompt
export const updateAIPrompt = async (id, promptData) => {
  const response = await axios.put(`${API_URL}/ai/prompts/${id}`, promptData);
  return response.data;
};

// Delete an AI prompt
export const deleteAIPrompt = async (id) => {
  const response = await axios.delete(`${API_URL}/ai/prompts/${id}`);
  return response.data;
};

// ============ CALLS & TRANSCRIPTION ============

// Get all calls with filtering
export const getCalls = async (params = {}) => {
  const response = await axios.get(`${API_URL}/calls`, { params });
  return response.data;
};

// Get a single call
export const getCall = async (id) => {
  const response = await axios.get(`${API_URL}/calls/${id}`);
  return response.data;
};

// Create a new call
export const createCall = async (callData) => {
  const response = await axios.post(`${API_URL}/calls`, callData);
  return response.data;
};

// Update a call
export const updateCall = async (id, callData) => {
  const response = await axios.put(`${API_URL}/calls/${id}`, callData);
  return response.data;
};

// Delete a call
export const deleteCall = async (id) => {
  const response = await axios.delete(`${API_URL}/calls/${id}`);
  return response.data;
};

// Get call statistics
export const getCallStats = async (params = {}) => {
  const response = await axios.get(`${API_URL}/calls/stats`, { params });
  return response.data;
};

// Get call recording
export const getCallRecording = async (id) => {
  const response = await axios.get(`${API_URL}/calls/${id}/recording`, {
    responseType: 'blob'
  });
  return response.data;
};

// Start transcription for a call
export const startTranscription = async (id, options = {}) => {
  const response = await axios.post(`${API_URL}/calls/${id}/transcribe`, options);
  return response.data;
};

// Get transcription status
export const getTranscriptionStatus = async (id) => {
  const response = await axios.get(`${API_URL}/calls/${id}/transcription`);
  return response.data;
};

// Start AI analysis for a call
export const startAnalysis = async (id, options = {}) => {
  const response = await axios.post(`${API_URL}/calls/${id}/analyze`, options);
  return response.data;
};

// Get supported transcription providers
export const getTranscriptionProviders = async () => {
  const response = await axios.get(`${API_URL}/calls/providers`);
  return response.data;
};

// Get provider pricing
export const getProviderPricing = async (provider) => {
  const response = await axios.get(`${API_URL}/calls/providers/${provider}/pricing`);
  return response.data;
};

// Bulk process calls
export const bulkProcessCalls = async (callIds, options = {}) => {
  const response = await axios.post(`${API_URL}/calls/bulk/process`, {
    call_ids: callIds,
    ...options
  });
  return response.data;
};

// Update QA score
export const updateQAScore = async (id, scoreData) => {
  const response = await axios.put(`${API_URL}/calls/${id}/qa`, scoreData);
  return response.data;
};

// Update recording path for a call
export const updateRecordingPath = async (id) => {
  const response = await axios.put(`${API_URL}/calls/${id}/recording-path`);
  return response.data;
};

// Get lead flow history for a call
export const getCallLeadFlow = async (id) => {
  const response = await axios.get(`${API_URL}/calls/${id}/lead-flow`);
  return response.data;
};

// Move lead to QA
export const moveLeadToQA = async (id) => {
  const response = await axios.put(`${API_URL}/calls/${id}/move-to-qa`);
  return response.data;
};

// Send lead to Vicidial API
export const sendLeadToVicidialAPI = async (id, notes) => {
  const response = await axios.post(`${API_URL}/leads/${id}/send-to-vicidial`, { notes });
  return response.data;
};

// Send lead to Qualitron API
export const sendLeadToQualitronAPI = async (id, notes) => {
  const response = await axios.post(`${API_URL}/leads/${id}/send-to-qualitron`, { notes });
  return response.data;
};
